import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, ArrowRight, Briefcase, MapPin, DollarSign, FileText, CheckCircle2, ChevronRight, Plus, X, Building2, RefreshCw } from 'lucide-react';
import { useUpdateJobPosting, useJobCategories, useJob } from '@/hooks/useJob';
import { useCompanies } from '@/hooks/useCompany';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/misc';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { NumberInput } from '@/components/ui/numberInput';
import AddressSelector from '@/components/common/AddressSelector';
import { Spinner } from '@/components/common';
import dayjs from '@/lib/dayjs';
import { Autocomplete, TextField } from '@mui/material';

/* ─── constants ─── */
const SUGGESTED_INDUSTRIES = [
  "Nhà hàng", "Quán cà phê", "Quán nhậu", "Trà sữa", "Tiệm bánh", "Bar/Pub", "Food Court", "Khách sạn/Resort", "Suất ăn công nghiệp"
];

const SUGGESTED_SKILLS = [
  "Quản lý nhà hàng", "Pha chế (Bartender)", "Pha chế cà phê (Barista)", "Phục vụ bàn", "Lên thực đơn (Menu planning)", "Nấu ăn (Bếp Á)", "Nấu ăn (Bếp Âu)", "Làm bánh (Bakery/Pastry)", "Chăm sóc khách hàng", "Quản lý chi phí (Food cost)", "Kiểm soát chất lượng", "Tuyển dụng & Đào tạo", "Tiếng Anh giao tiếp", "Marketing F&B", "Quản lý kho & Nhập hàng", "Kỹ năng bán hàng (Sales)", "Giải quyết tình huống"
];

const SUGGESTED_BENEFITS = [
  "Bảo hiểm Y tế / BHXH", "Lương tháng 13", "Thưởng doanh thu / Phí phục vụ (Service charge)", "Bao cơm ca", "Hỗ trợ gửi xe / xăng xe", "Đồng phục", "Khám sức khỏe định kỳ", "Du lịch / Đào tạo hàng năm", "Lộ trình thăng tiến rõ ràng", "Nghỉ phép năm (12 ngày+)", "Linh hoạt ca làm việc"
];
const JOB_TYPES = ['Full-time', 'Part-time', 'Freelance', 'Internship', 'Remote', 'Hybrid'].map((v) => ({ value: v, label: v }));
const CURRENCY = [
  { value: 'VND', label: 'VND' },
  { value: 'USD', label: 'USD' },
];
const TIME_UNITS = [
  { value: 'month', label: 'Tháng' },
  { value: 'year', label: 'Năm' },
];
const STEPS = ['Vị trí & Công ty', 'Địa điểm', 'Lương & Thời hạn', 'Mô tả', 'Kỹ năng & Phúc lợi'];

/* ─── schema ─── */
const schema = z.object({
  // Step 0
  title: z.string().min(5, 'Tối thiểu 5 ký tự'),
  jobCategoryId: z.string().min(1, 'Nhập category ID'),
  jobCategoryName: z.string().min(1, 'Nhập category name'),

  jobType: z.string().optional(),
  companyId: z.string().optional(),
  companyName: z.string().optional(),
  companySize: z.string().optional(),
  companyType: z.string().optional(),
  companyWebsite: z.string().url().optional().or(z.literal('')),
  companyAddress: z.string().optional(),
  // Step 1
  streetAddress: z.string().optional(),
  workingLocation: z.string().optional(),
  countryId: z.coerce.number({ invalid_type_error: 'Vui lòng chọn quốc gia' }).min(1, 'Vui lòng chọn quốc gia'),
  provinceId: z.coerce.number({ invalid_type_error: 'Vui lòng chọn tỉnh/thành phố' }).min(1, 'Vui lòng chọn tỉnh/thành phố'),
  districtId: z.coerce.number({ invalid_type_error: 'Vui lòng chọn quận/huyện' }).min(1, 'Vui lòng chọn quận/huyện'),
  wardId: z.coerce.number({ invalid_type_error: 'Vui lòng chọn phường/xã' }).min(1, 'Vui lòng chọn phường/xã'),
  // Step 2
  salaryMin: z
    .any()
    .transform((val) => {
      if (typeof val === 'number') return val;
      return val ? Number(String(val).replace(/\D/g, '')) : 0;
    })
    .refine((val) => val > 0, { message: 'Lương min phải > 0' }),
  salaryMax: z
    .any()
    .transform((val) => {
      if (typeof val === 'number') return val;
      return val ? Number(String(val).replace(/\D/g, '')) : 0;
    })
    .refine((val) => val > 0, { message: 'Lương max phải > 0' }),
  currencyUnit: z.string().default('VND'),
  timeUnit: z.string().default('month'),
  vacancies: z
    .any()
    .transform((val) => {
      if (typeof val === 'number') return val;
      return val ? Number(String(val).replace(/\D/g, '')) : undefined;
    }),
  deadline: z.string().optional(),
  probationMonths: z
    .any()
    .transform((val) => {
      if (typeof val === 'number' || val === undefined) return val;
      return val ? Number(String(val).replace(/\D/g, '')) : undefined;
    }),
  workingHours: z.string({ required_error: 'Vui lòng nhập giờ làm việc' }).min(1, 'Vui lòng nhập giờ làm việc'),
  // Step 3
  description: z.string().min(10, 'Vui lòng nhập tối thiểu 10 ký tự'),
  educationRequirement: z.string().optional(),
  experienceLevel: z.string().optional(),
  // Step 4
  skills: z.array(z.object({ value: z.string() })).optional(),
  benefits: z.array(z.object({ value: z.string() })).optional(),
  industries: z.array(z.object({ value: z.string() })).optional(),
  areaUnit: z.string().default('m²'),
});
type FormData = z.infer<typeof schema>;

const STEP_FIELDS: (keyof FormData)[][] = [['title', 'jobCategoryId', 'jobCategoryName'], [], ['salaryMin', 'salaryMax', 'workingHours'], ['description'], []];

export default function EditJobPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { addToast } = useUIStore();

  // Queries
  const { data: job, isLoading: loadingJob } = useJob(id!);
  const { mutate: update, isPending } = useUpdateJobPosting();
  const { data: companiesRes } = useCompanies({ createdBy: user?.id });
  const { data: categories } = useJobCategories();

  const [step, setStep] = useState(0);


  const {
    register,
    handleSubmit,
    trigger,
    watch,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      countryId: 1,
      currencyUnit: 'VND',
      timeUnit: 'month',
      areaUnit: 'm²',
      skills: [],
      benefits: [],
      industries: [],
      vacancies: 1,
      jobType: 'Full-time',
      salaryMin: 0,
      salaryMax: 0,
      jobCategoryId: '',
      jobCategoryName: '',
      companyId: ''
    },
  });



  // Auto-fill form values when job data is loaded
  useEffect(() => {
    if (job) {
      let initialSkills: { value: string }[] = [];
      if (Array.isArray(job.skills)) initialSkills = job.skills.map(s => ({ value: s }));
      else if (typeof job.skills === 'string' && job.skills) initialSkills = job.skills.split(',').map(s => ({ value: s.trim() }));

      let initialBenefits: { value: string }[] = [];
      if (Array.isArray(job.benefits)) initialBenefits = job.benefits.map(b => ({ value: b }));
      else if (typeof job.benefits === 'string' && job.benefits) initialBenefits = job.benefits.split('\\n').map(b => ({ value: b.replace(/^- /, '').trim() }));

      let initialIndustries: { value: string }[] = [];
      // @ts-ignore
      if (Array.isArray(job.industries)) initialIndustries = job.industries.map(i => ({ value: i }));
      // @ts-ignore
      else if (typeof job.industries === 'string' && job.industries) initialIndustries = job.industries.split(',').map(i => ({ value: i.trim() }));

      reset({
        title: job.title || '',
        jobCategoryId: job.jobCategoryId || '',
        // @ts-ignore
        jobCategoryName: job.jobCategoryName || '',
        jobType: job.jobType || 'Full-time',
        workingLocation: job.workingLocation || '',
        countryId: job.countryId || 1,
        provinceId: job.provinceId,
        districtId: job.districtId,
        wardId: job.wardId,
        salaryMin: job.salaryMin ? Number(job.salaryMin) : 0,
        salaryMax: job.salaryMax ? Number(job.salaryMax) : 0,
        currencyUnit: job.currencyUnit || 'VND',
        timeUnit: job.timeUnit || 'month',
        vacancies: job.vacancies || 1,
        deadline: job.deadline ? dayjs(job.deadline).format('YYYY-MM-DD') : '',
        probationMonths: job.probationMonths || undefined,
        workingHours: job.workingHours || '',
        description: job.description || '',
        educationRequirement: job.educationRequirement || '',
        experienceLevel: job.experienceLevel || '',
        // @ts-ignore
        companyId: job.companyId || '',
        skills: initialSkills,
        benefits: initialBenefits,
        industries: initialIndustries
      });
    }
  }, [job, reset]);

  const companies = companiesRes?.data || [];
  const companyOptions = companies.map(c => ({ value: c.id, label: c.name }));
  const categoryOptions = categories?.map(c => ({ value: c.id, label: c.name })) || [];

  const onCategoryChange = (id: string) => {
    const cat = categories?.find(c => c.id === id);
    if (!cat) return;
    setValue('jobCategoryId', cat.id, { shouldValidate: true });
    setValue('jobCategoryName', cat.name, { shouldValidate: true });
  };

  const onCompanyChange = (id: string) => {
    const comp = companies.find(c => c.id === id);
    if (!comp) return;

    setValue('companyId', comp.id, { shouldValidate: true });
    setValue('companyName', comp.name, { shouldValidate: true });
    setValue('companySize', comp.size, { shouldValidate: true });
    setValue('companyType', comp.businessTypeName, { shouldValidate: true });
    setValue('companyWebsite', comp.website || '', { shouldValidate: true });
    setValue('companyAddress', comp.address, { shouldValidate: true });

    if (comp.industry) {
      setValue('industries', [{ value: comp.industry }], { shouldValidate: true });
      const matchingCat = categories?.find(c =>
        c.name.toLowerCase() === comp.industry.toLowerCase() ||
        comp.industry.toLowerCase().includes(c.name.toLowerCase())
      );
      if (matchingCat) {
        setValue('jobCategoryId', matchingCat.id, { shouldValidate: true });
        setValue('jobCategoryName', matchingCat.name, { shouldValidate: true });
      }
    }
  };

  const nextStep = async () => {
    const fields = STEP_FIELDS[step];
    const valid = await trigger(fields as any);
    if (valid) {
      setStep((s) => Math.min(s + 1, STEPS.length - 1));
    }
  };



  const onSubmit = (data: FormData) => {
    const payload = {
      ...data,
      skills: data.skills?.map((s) => s.value) ?? [],
      benefits: data.benefits?.map((b) => b.value) ?? [],
      industries: data.industries?.map((i) => i.value) ?? [],
      companyWebsite: data.companyWebsite || undefined,
      deadline: data.deadline ? new Date(data.deadline).toISOString() : undefined,
    };
    update({ id: id!, data: payload as any }, {
      onSuccess: () => {
        navigate('/viec-lam/quan-ly?tab=postings');
      }
    });
  };

  if (loadingJob) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Spinner size="lg" />
        <h2 className="text-xl font-bold text-gray-800">Đang tải thông tin...</h2>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto animate-fade-in pb-10">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link to="/viec-lam/quan-ly">
          <Button variant="ghost" className="p-2 border border-gray-200 rounded-xl hover:bg-gray-50">
            <ArrowLeft size={16} /> Quay lại
          </Button>
        </Link>
        <div>
          <h1 className="font-display font-bold text-2xl" style={{ color: 'var(--text-1)' }}>
            Chỉnh sửa tin tuyển dụng
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-3)' }}>
            Cập nhật lại thông tin tuyển dụng của bạn
          </p>
        </div>
      </div>

      {/* Steps */}
      <div className="flex items-center gap-0 mb-8 overflow-x-auto">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center shrink-0">
            <div
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer"
              style={{
                background: i === step ? 'var(--brand)' : i < step ? '#16a34a' : 'var(--surface-2)',
                color: i <= step ? '#fff' : 'var(--text-3)',
              }}
              onClick={() => i < step && setStep(i)}
            >
              {i < step ? <CheckCircle2 size={12} /> : <span>{i + 1}</span>}
              <span className="hidden sm:inline">{s}</span>
            </div>
            {i < STEPS.length - 1 && <ChevronRight size={14} className="mx-1" style={{ color: 'var(--text-3)' }} />}
          </div>
        ))}
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* ── Step 0: Position & Company ── */}
        {step === 0 && (
          <>
            <FormCard title="Thông tin vị trí" icon={<Briefcase size={16} />}>
              <Input label="Tên vị trí *" placeholder="VD: Head Chef, Store Manager" error={errors.title?.message} {...register('title')} />
              <div className="grid grid-cols-2 gap-4">
                <Controller
                  name="jobCategoryId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label="Ngành nghề *"
                      options={categoryOptions}
                      placeholder="Chọn ngành nghề..."
                      error={errors.jobCategoryId?.message}
                      value={field.value}
                      onChange={(e) => {
                        const val = e.target.value;
                        field.onChange(val);
                        onCategoryChange(val);
                      }}
                    />
                  )}
                />
                <Select label="Loại công việc" options={JOB_TYPES} placeholder="Chọn loại" {...register('jobType')} />
              </div>

            </FormCard>

            <FormCard title="Thông tin công ty" icon={<Building2 size={16} />} className="mt-4">
              <Controller
                name="companyId"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Chọn công ty *"
                    options={companyOptions}
                    placeholder="Tìm công ty..."
                    error={errors.companyId?.message}
                    value={field.value}
                    onChange={(e) => {
                      const val = e.target.value;
                      field.onChange(val);
                      onCompanyChange(val);
                    }}
                  />
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <Input label="Quy mô" placeholder="Tự động điền..." value={watch('companySize') || ''} readOnly disabled />
                <Input label="Website" placeholder="Tự động điền..." value={watch('companyWebsite') || ''} readOnly disabled />
              </div>
              <Input label="Địa chỉ công ty" placeholder="Tự động điền..." value={watch('companyAddress') || ''} readOnly disabled />
            </FormCard>
          </>
        )}

        {/* ── Step 1: Location ── */}
        {step === 1 && (
          <FormCard title="Địa điểm làm việc" icon={<MapPin size={16} />}>
            <Input label="Địa chỉ cụ thể" placeholder="123 Nguyễn Huệ, Quận 1" {...register('streetAddress')} />
            <Input label="Địa điểm làm việc" placeholder="Remote / Hà Nội / TP.HCM" {...register('workingLocation')} />

            <div className="pt-4 border-t border-gray-100">
              <AddressSelector
                countryId={watch('countryId')}
                provinceId={watch('provinceId')}
                districtId={watch('districtId')}
                wardId={watch('wardId')}
                onCountryChange={(id) => setValue('countryId', id, { shouldValidate: true })}
                onProvinceChange={(id) => setValue('provinceId', id, { shouldValidate: true })}
                onDistrictChange={(id) => setValue('districtId', id, { shouldValidate: true })}
                onWardChange={(id) => setValue('wardId', id, { shouldValidate: true })}
                errors={{
                  countryId: errors.countryId?.message,
                  provinceId: errors.provinceId?.message,
                  districtId: errors.districtId?.message,
                  wardId: errors.wardId?.message,
                }}
                required
              />
            </div>
          </FormCard>
        )}

        {/* ── Step 2: Salary & Schedule ── */}
        {step === 2 && (
          <FormCard title="Lương & Lịch làm việc" icon={<DollarSign size={16} />}>
            <div className="grid grid-cols-3 gap-4">

              <NumberInput label="Lương tối thiểu *" placeholder="1000" error={errors.salaryMin?.message} {...register('salaryMin')} />
              <NumberInput label="Lương tối đa *" placeholder="2000" error={errors.salaryMax?.message} {...register('salaryMax')} />
              <Select label="Đơn vị tiền" options={CURRENCY} {...register('currencyUnit')} />
            </div>
            <Select label="Tính theo" options={TIME_UNITS} {...register('timeUnit')} />
            <div className="grid grid-cols-2 gap-4">
              <NumberInput label="Số lượng tuyển" placeholder="3" {...register('vacancies')} />
              <NumberInput label="Thử việc (tháng)" placeholder="2" {...register('probationMonths')} />
              <Input label="Giờ làm việc *" placeholder="9AM - 6PM" error={errors.workingHours?.message} {...register('workingHours')} />
              <Input label="Hạn nộp hồ sơ" type="date" {...register('deadline')} />
            </div>
          </FormCard>
        )}

        {/* ── Step 3: Description ── */}
        {step === 3 && (
          <FormCard title="Mô tả công việc" icon={<FileText size={16} />}>
            <Textarea
              label="Mô tả chi tiết *"
              rows={6}
              placeholder="Mô tả công việc, nhiệm vụ hàng ngày, môi trường làm việc..."
              error={errors.description?.message}
              {...register('description')}
            />
            <Input label="Trình độ học vấn" placeholder="Bachelor's Degree" {...register('educationRequirement')} />
            <Input label="Kinh nghiệm yêu cầu" placeholder="2+ years" {...register('experienceLevel')} />
          </FormCard>
        )}

        {/* ── Step 4: Skills & Benefits ── */}
        {step === 4 && (
          <>
            {/* Industries */}
            <FormCard title="Ngành nghề" icon={<Briefcase size={16} />}>
              <Controller
                name="industries"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    multiple
                    freeSolo
                    options={SUGGESTED_INDUSTRIES}
                    value={field.value?.map((s) => s.value) || []}
                    onChange={(_, data) => field.onChange(data.map((v) => ({ value: v })))}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="VD: Nhà hàng, Quán cà phê (Enter để thêm)"
                        error={!!errors.industries}
                        helperText={errors.industries?.message}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            bgcolor: '#f8fafc',
                            '& fieldset': { borderColor: '#e2e8f0' },
                            '&:hover fieldset': { borderColor: '#f97316' },
                            '&.Mui-focused fieldset': { borderColor: '#f97316' },
                          }
                        }}
                      />
                    )}
                  />
                )}
              />
            </FormCard>

            {/* Skills */}
            <FormCard title="Kỹ năng yêu cầu" icon={<CheckCircle2 size={16} />} className="mt-4">
              <Controller
                name="skills"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    multiple
                    freeSolo
                    options={SUGGESTED_SKILLS}
                    value={field.value?.map((s) => s.value) || []}
                    onChange={(_, data) => field.onChange(data.map((v) => ({ value: v })))}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="VD: Quản lý nhà hàng, Pha chế... (Enter để thêm)"
                        error={!!errors.skills}
                        helperText={errors.skills?.message}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            bgcolor: '#f8fafc',
                            '& fieldset': { borderColor: '#e2e8f0' },
                            '&:hover fieldset': { borderColor: '#f97316' },
                            '&.Mui-focused fieldset': { borderColor: '#f97316' },
                          }
                        }}
                      />
                    )}
                  />
                )}
              />
            </FormCard>

            {/* Benefits */}
            <FormCard title="Phúc lợi" icon={<CheckCircle2 size={16} />} className="mt-4">
              <Controller
                name="benefits"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    multiple
                    freeSolo
                    options={SUGGESTED_BENEFITS}
                    value={field.value?.map((s) => s.value) || []}
                    onChange={(_, data) => field.onChange(data.map((v) => ({ value: v })))}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="VD: Bao cơm ca, Thưởng tháng 13... (Enter để thêm)"
                        error={!!errors.benefits}
                        helperText={errors.benefits?.message}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            bgcolor: '#f8fafc',
                            '& fieldset': { borderColor: '#e2e8f0' },
                            '&:hover fieldset': { borderColor: '#f97316' },
                            '&.Mui-focused fieldset': { borderColor: '#f97316' },
                          }
                        }}
                      />
                    )}
                  />
                )}
              />
            </FormCard>
          </>
        )}

        {/* Navigation */}
        <div className="flex justify-between gap-3 mt-6">
          <div>
            {step > 0 && (
              <Button type="button" variant="outline" onClick={() => setStep((s) => s - 1)}>
                <ArrowLeft size={14} /> Quay lại
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="ghost">
              <Link to="/viec-lam/quan-ly">Hủy</Link>
            </Button>
            {step < STEPS.length - 1 ? (
              <Button key="btn-next" type="button" variant="brand" onClick={nextStep}>
                Tiếp theo <ArrowRight size={14} />
              </Button>
            ) : (
              <Button key="btn-submit" type="submit" variant="brand" loading={isPending}>
                Lưu thay đổi
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}

/* ─── helper components ─── */
function FormCard({ title, icon, children, className }: { title: string; icon: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('rounded-2xl border p-6 space-y-4', className)} style={{ background: 'var(--surface-0)', borderColor: 'var(--border-c)' }}>
      <h2 className="font-display font-bold text-base flex items-center gap-2 pb-3 border-b" style={{ color: 'var(--text-1)', borderColor: 'var(--border-c)' }}>
        <span style={{ color: 'var(--brand)' }}>{icon}</span> {title}
      </h2>
      {children}
    </div>
  );
}


